import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { PosDetailWithOrders, PosList } from '../../../models/pos.model';
import { AddItemToTable, NosPosList, NosPosOrderDetail, NosTableTransfer, OpenTableData } from '../models/pos.model';
import { NosAccount } from '../models/account.model';
import { NosInvoice } from '../models/invoice.model';
import { isNull } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class PosServiceAdapter {
  private _posList: NosPosList;
  private _posDetails$ = new BehaviorSubject<PosDetailWithOrders | null>(null);
  private _currentPosId: string | null = null; // Track current POS ID
  private _isCreatingDefaultOrder = false;

  constructor(private _httpClient: HttpClient) {}

  getPosList(): Observable<PosList> {
    return this._httpClient.post<NosPosList>('api/getTable', {}).pipe(
      map(nosPosList => {
        this._posList = nosPosList;
        return nosPosList.map(nosPos => ({
          id: nosPos.id.toString(),
          name: nosPos.nome,
          status: nosPos.status
        }));
      })
    );
  }

  getPosOrders(posId: string): Observable<{ id: number; name: string }[]> {
    return this._httpClient.get<{ data: NosAccount[] }>('api/sellpoints/' + posId + '/accounts').pipe(
      map(res => res.data),
      map((accounts: NosAccount[]) =>
        accounts.map(account => ({
          id: account.id,
          name: account.nome
        }))
      )
    );
  }

  getPosCurrentSaleSessionOrdersWithDetails(posId: string): Observable<PosDetailWithOrders | null> {
    // Clear stored data if POS ID has changed
    if (this._currentPosId !== posId) {
      this.clearStoredData();
      this._currentPosId = posId;
    }

    this.loadPosDetails(posId); // Trigger initial load
    return this._posDetails$.asObservable().pipe(filter(result => !isNull(result))); // Return observable
  }

  private clearStoredData(): void {
    this._posDetails$.next(null);
  }

  private loadPosDetails(posId: string) {
    this.getPosList()
      .pipe(
        switchMap(posList => {
          const pos = posList.find(po => po.id.toString() === posId);
          return this.getPosOrders(posId).pipe(
            switchMap(orders =>
              this._httpClient.get<NosPosOrderDetail>('api/get-request/' + posId).pipe(
                map(nosPosOrderDetail => ({
                  pos: {
                    id: posId,
                    name: pos?.name ?? '',
                    status: nosPosOrderDetail.status.toString(),
                    token: nosPosOrderDetail.token
                  },
                  orders: orders.map(account => {
                    const items = nosPosOrderDetail?.grouped_array?.[account.name] ?? [];
                    return {
                      name: account.name,
                      id: account.id.toString(),
                      items: items.map(item => ({
                        id: item.id.toString(),
                        product: {
                          name: item.nome,
                          id: item.id_produto.toString(),
                          price: { EUR: item.preco },
                          priceWithoutTax: { EUR: item.precoOriginal },
                          vatRate: item.iva,
                          vatTreatment: item.iva_treatment
                        },
                        quantity: item.quant
                      }))
                    };
                  })
                })),
                tap(detail => this._posDetails$.next(detail)),
                catchError(error => {
                  console.error('Failed to load POS details', error);
                  return of(null);
                })
              )
            )
          );
        })
      )
      .subscribe();
  }

  refreshPosDetails(posId: string) {
    this.loadPosDetails(posId);
  }

  createPosOrder(posId: string, accountName: string, geral?: boolean): Observable<any> {
    return this._httpClient
      .post<any>('api/sellpoints/' + posId + '/accounts', { nome: accountName, geral })
      .pipe(tap(() => this.refreshPosDetails(posId)));
  }

  updatePosOrder(posId: string, accountId: string, accountName: string): Observable<any> {
    return this._httpClient
      .put('api/sellpoints/' + Number(posId) + '/accounts/' + Number(accountId), { name: accountName })
      .pipe(tap(() => this.refreshPosDetails(posId)));
  }

  deletePosOrder(posId: string, accountId: string): Observable<any> {
    return this._httpClient
      .delete('api/sellpoints/' + posId + '/accounts/' + Number(accountId))
      .pipe(tap(() => this.refreshPosDetails(posId)));
  }

  addTable(param: { nome: string; id_mesa?: string }): Observable<NosPosList> {
    return this._httpClient.post<NosPosList>('api/create-table', param);
  }

  transferTable(param: NosTableTransfer): Observable<any> {
    return this._httpClient.put<any>('api/transferTable', param);
  }

  addRequest(param: AddItemToTable): Observable<any> {
    return this._httpClient.post<any>('api/add-request', param).pipe(tap(() => this.refreshPosDetails(param.id_mesa.toString())));
  }

  openTable(param: OpenTableData): Observable<any> {
    return this._httpClient.post<any>('api/opentable', param);
  }

  doPayment(param: NosInvoice): Observable<any> {
    return this._httpClient.post<any>('api/fatura-restaurant', param);
  }

  // Download invoice PDF by receipt ID
  downloadInvoice(receiptId: string | number): Observable<Blob> {
    return this._httpClient.get(`api/invoice/${receiptId}/download`, { responseType: 'blob' });
  }

  createDefaultOrderForPos(id: string): Observable<any> {
    if (this._isCreatingDefaultOrder) {
      return of(null);
    }
    this._isCreatingDefaultOrder = true;
    return this.createPosOrder(id, 'Conta Geral', true).pipe(
      finalize(() => {
        this._isCreatingDefaultOrder = false;
      })
    );
  }
}
